import './style.scss';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEffect, useMemo, useState } from 'react';
import PdfIcon from '../../assets/icons/pdfIcon.svg';
import BlueEyeIcon from '../../assets/icons/blueEye.svg';
import ArrowGreyIcon from '../../assets/icons/arrowGrey.svg';
import ArrowBlueIcon from '../../assets/icons/arrowBlue.svg';
import CustomFilterSelect from '../../shared/customFilterSelect';
import AdminProcessScopeFilter from '../../components/adminProcessScopeFilter';
import { getAllMeetingEvidences, getSchools } from '../../API/apiService';
import {
  buildProtectedImageUrl,
  fetchWithAuth
} from '../../utils/secureMedia';
import { getCurrentIsoDateInBogota } from '../../utils/dateTime';
import { buildEvidenceDownloadName } from '../../utils/evidenceFileName';
import { resolveEvidenceSlot } from '../../utils/evidenceSlot';
import { buildBatchedEvidenceArchive } from '../../utils/evidenceArchive';
import { ENTITY_TYPE_FILTER_OPTIONS, getEntityTypeFilterLabel, matchesEntityType } from '../../utils/entityType';
import {
  ADMIN_SCOPE_VALUES,
  COP_MEETING_TYPES,
  isCopMeetingType,
  matchesEvidenceByAdminScope,
  normalizeAdminScope
} from '../../utils/adminScope';
import { getMultiFilterLabel, matchesAnySelected, normalizeMultiSelection, parseMultiFilterValue, serializeMultiFilterValue } from '../../utils/multiFilter';

const ALL_FILTER_ID = '__all__';
const EVIDENCE_SORT_FIELDS = {
  DATE: 'date'
};

const meetingTypeOptions = [
  { id: 'concertacion', name: 'Concertación' },
  { id: 'socializacion', name: 'Socialización' },
  { id: 'encuentro-1', name: 'Encuentro 1' },
  { id: 'encuentro-2', name: 'Encuentro 2' },
  { id: 'encuentro-3', name: 'Encuentro 3' },
  { id: 'encuentro-4', name: 'Encuentro 4' },
  { id: 'encuentro-5', name: 'Encuentro 5' },
  { id: 'encuentro-6', name: 'Encuentro 6' },
  { id: 'encuentro-7', name: 'Encuentro 7' },
  { id: 'encuentro-8', name: 'Encuentro 8' },
  { id: 'accion-incidencia', name: 'Acci\u00f3n de incidencia' },
  { id: 'cop-solicitud', name: 'COP - Solicitud' },
  { id: 'cop-planeacion', name: 'COP - Planeacion' },
  { id: 'cop-desarrollo', name: 'COP - Desarrollo' },
  { id: 'cop-cierre', name: 'COP - Cierre' }
];

const ADVISOR_PHASE_BY_TYPE = {
  COP_SOLICITUD: 'solicitud',
  COP_PLANEACION: 'planeacion',
  COP_DESARROLLO: 'desarrollo',
  COP_CIERRE: 'cierre'
};

const ADVISOR_PHASE_EVIDENCE_PREFIXES = {
  solicitud: ['SolC', 'AcC', 'AsC'],
  planeacion: ['AcC', 'GuC', 'AsC'],
  desarrollo: ['AcC', 'GuC', 'AsC'],
  cierre: ['ValC', 'AcC', 'AsC']
};

function normalizeDateValue(value) {
  return String(value || '').trim().split('T')[0];
}

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function buildUniqueNamedOptions(values) {
  const unique = new Map();

  values.forEach(value => {
    const name = String(value || '').trim();
    if (!name) return;

    unique.set(name, {
      id: name,
      name
    });
  });

  return Array.from(unique.values()).sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), 'es', {
      sensitivity: 'base'
    })
  );
}

function buildMediatorOptions(items) {
  const unique = new Map();

  items.forEach(item => {
    const mediators = Array.isArray(item?.mediators) && item.mediators.length > 0
      ? item.mediators
      : [{ id: item?.mediatorId, name: item?.mediatorName }];
    mediators.forEach(mediator => {
      const id = String(mediator?.id ?? '').trim();
      const name = String(mediator?.name ?? '').trim();
      if (!id || !name) return;

      unique.set(id, { id, name });
    });
  });

  return Array.from(unique.values()).sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), 'es', {
      sensitivity: 'base'
    })
  );
}

function getEvidenceMeetingTypeOptionId(evidence) {
  const type = String(evidence?.type || '').toUpperCase();

  if (type === 'INCIDENCE_ACTION') {
    return 'accion-incidencia';
  }

  if (type === 'CONC' || type === 'EXP') {
    return 'concertacion';
  }

  if (type === 'SOC') {
    return 'socializacion';
  }

  if (type === 'MEETING') {
    const order = Number(evidence?.order);
    if (Number.isInteger(order) && order > 0) {
      return `encuentro-${order}`;
    }
  }

  if (ADVISOR_PHASE_BY_TYPE[type]) {
    return `cop-${ADVISOR_PHASE_BY_TYPE[type]}`;
  }

  return '';
}

function getEvidenceMediatorIds(evidence) {
  const mediatorIds = Array.isArray(evidence?.mediatorIds)
    ? evidence.mediatorIds
    : [evidence?.mediatorId];
  return mediatorIds.map(id => String(id ?? '').trim()).filter(Boolean);
}

function getEvidenceValues(evidence, pluralKey, singularKey) {
  const values = Array.isArray(evidence?.[pluralKey])
    ? evidence[pluralKey]
    : [evidence?.[singularKey]];
  return values.map(value => String(value ?? '').trim()).filter(Boolean);
}

function getEvidenceSchoolName(evidence) {
  return String(
    evidence?.schoolName ?? evidence?.school?.name ?? evidence?.school?.address ?? ''
  ).trim();
}

function resolveFileExtension(image) {
  const explicitType = String(image?.type || '').toLowerCase();
  if (explicitType === 'pdf') return 'pdf';
  if (explicitType === 'jpeg') return 'jpg';
  if (explicitType) return explicitType;

  const fileName = String(image?.name || '');
  const nameMatch = fileName.match(/\.([a-z0-9]+)$/i);
  if (!nameMatch?.[1]) return 'jpg';
  return String(nameMatch[1]).toLowerCase();
}

function getEvidencePrefix(evidence, index) {
  const normalizedType = String(evidence?.type || '').trim().toUpperCase();

  if (isCopMeetingType(normalizedType)) {
    const advisorPhase = ADVISOR_PHASE_BY_TYPE[normalizedType];
    const prefixes = ADVISOR_PHASE_EVIDENCE_PREFIXES[advisorPhase] || [];
    return prefixes[index] ?? `C${index + 1}`;
  }

  if (normalizedType === 'MEETING') {
    const prefixes = ['C', 'GuC', 'AsC'];
    return prefixes[index] ?? `C${index + 1}`;
  }

  return index === 1 ? 'AcC' : index === 2 ? 'AsC' : 'C';
}

function resolveEvidenceSlotIndex(imageName, fallbackIndex) {
  const slot = resolveEvidenceSlot(imageName, fallbackIndex + 1, 3);
  return slot - 1;
}

function downloadBlob(blob, fileName) {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

function parseEvidenceDateSortValue(value) {
  const normalized = normalizeDateValue(value);
  if (!normalized) return null;

  const timestamp = Date.parse(`${normalized}T00:00:00`);
  return Number.isNaN(timestamp) ? null : timestamp;
}

function compareNullableValues(leftValue, rightValue, direction, compareDefinedValues) {
  const leftIsEmpty =
    leftValue === null || leftValue === undefined || String(leftValue).trim() === '';
  const rightIsEmpty =
    rightValue === null || rightValue === undefined || String(rightValue).trim() === '';

  if (leftIsEmpty && rightIsEmpty) return 0;
  if (leftIsEmpty) return 1;
  if (rightIsEmpty) return -1;

  const compareResult = compareDefinedValues(leftValue, rightValue);
  return direction === 'desc' ? compareResult * -1 : compareResult;
}

function buildAdminEvidenceFiltersSearch({
  scope,
  mediatorId,
  meetingTypeId,
  formativeProcessCode,
  schoolName,
  groupCode,
  advisorName,
  fromDate,
  toDate
}) {
  const searchParams = new URLSearchParams();

  if (scope) searchParams.set('scope', scope);
  if (mediatorId) searchParams.set('mediatorId', mediatorId);
  if (meetingTypeId) searchParams.set('meetingType', meetingTypeId);
  if (formativeProcessCode) searchParams.set('schoolCode', formativeProcessCode);
  if (schoolName) searchParams.set('schoolName', schoolName);
  if (groupCode) searchParams.set('groupCode', groupCode);
  if (advisorName) searchParams.set('advisorName', advisorName);
  if (fromDate) searchParams.set('from', fromDate);
  if (toDate) searchParams.set('to', toDate);

  return searchParams.toString();
}

function getEvidenceDetailPath(evidence, returnTo) {
  const type = String(evidence?.type || '').toUpperCase();

  if (type === 'INCIDENCE_ACTION') {
    return `/acciones-incidencia/${evidence?.id}`;
  }

  if (type === 'CONC' || type === 'EXP') {
    const searchParams = new URLSearchParams({
      origin: 'admin-evidences'
    });

    if (returnTo) {
      searchParams.set('returnTo', returnTo);
    }

    return `/exploracion/${evidence?.schoolId}?${searchParams.toString()}`;
  }

  if (type === 'SOC') {
    const searchParams = new URLSearchParams({
      origin: 'admin-evidences',
      number: String(Number(evidence?.order) === 2 ? 2 : 1)
    });

    if (returnTo) {
      searchParams.set('returnTo', returnTo);
    }

    return `/socializacion/${evidence?.schoolId}?${searchParams.toString()}`;
  }

  if (type === 'MEETING') {
    const searchParams = new URLSearchParams({
      encuentro: String(evidence?.id ?? ''),
      origin: 'admin-evidences'
    });

    if (returnTo) {
      searchParams.set('returnTo', returnTo);
    }

    return `/escuelas/grupo/lista_de_encuentros/encuentro?${searchParams.toString()}`;
  }

  if (COP_MEETING_TYPES.includes(type)) {
    const searchParams = new URLSearchParams({
      encuentro: String(evidence?.id ?? ''),
      origin: 'admin-evidences'
    });
    const advisorPhase = ADVISOR_PHASE_BY_TYPE[type];

    if (advisorPhase) {
      searchParams.set('phase', advisorPhase);
      searchParams.set('order', String(evidence?.order ?? ''));
    }

    if (returnTo) {
      searchParams.set('returnTo', returnTo);
    }

    return `/escuelas/grupo/lista_de_encuentros/encuentro?${searchParams.toString()}`;
  }

  return '';
}

function EvidencesAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const adminScope = normalizeAdminScope(searchParams.get('scope'));
  const isCopScope = adminScope === ADMIN_SCOPE_VALUES.COP;
  const areGlobalDownloadsTemporarilyDisabled =
    adminScope === ADMIN_SCOPE_VALUES.PF ||
    adminScope === ADMIN_SCOPE_VALUES.NDN;
  const [evidences, setEvidences] = useState([]);
  const [schoolMetadataById, setSchoolMetadataById] = useState({});
  const [isLoadingEvidences, setIsLoadingEvidences] = useState(true);
  const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);
  const [isDownloadingUnifiedArchive, setIsDownloadingUnifiedArchive] = useState(false);
  const [isDownloadingFilteredArchive, setIsDownloadingFilteredArchive] = useState(false);
  const [isDownloadingFilteredUnifiedArchive, setIsDownloadingFilteredUnifiedArchive] =
    useState(false);
  const [evidenceSort, setEvidenceSort] = useState({
    field: '',
    direction: 'asc'
  });
  const isDownloadingAnyArchive =
    isDownloadingArchive ||
    isDownloadingUnifiedArchive ||
    isDownloadingFilteredArchive ||
    isDownloadingFilteredUnifiedArchive;
  const [selectedMediatorIds, setSelectedMediatorIds] = useState(() =>
    parseMultiFilterValue(searchParams.get('mediatorId'))
  );
  const [selectedMeetingTypeIds, setSelectedMeetingTypeIds] = useState(() =>
    parseMultiFilterValue(searchParams.get('meetingType'))
  );
  const [selectedFormativeProcessCodes, setSelectedFormativeProcessCodes] = useState(() =>
    parseMultiFilterValue(searchParams.get('schoolCode'))
  );
  const [selectedSchoolNames, setSelectedSchoolNames] = useState(() =>
    parseMultiFilterValue(searchParams.get('schoolName'))
  );
  const [selectedEntityTypes, setSelectedEntityTypes] = useState([]);
  const [selectedGroupCodes, setSelectedGroupCodes] = useState(() =>
    parseMultiFilterValue(searchParams.get(isCopScope ? 'advisorName' : 'groupCode'))
  );
  const [fromDate, setFromDate] = useState(() =>
    String(searchParams.get('from') || '').trim()
  );
  const [toDate, setToDate] = useState(() =>
    String(searchParams.get('to') || '').trim()
  );

  const currentFilters = useMemo(
    () => ({
      selectedMediatorIds,
      selectedMeetingTypeIds,
      selectedFormativeProcessCodes,
      selectedSchoolNames,
      selectedEntityTypes,
      selectedGroupCodes,
      fromDate,
      toDate
    }),
    [
      selectedMediatorIds,
      selectedMeetingTypeIds,
      selectedFormativeProcessCodes,
      selectedSchoolNames,
      selectedEntityTypes,
      selectedGroupCodes,
      fromDate,
      toDate
    ]
  );

  const scopedEvidences = useMemo(
    () =>
      evidences.filter(evidence =>
        matchesEvidenceByAdminScope(evidence, adminScope)
      ),
    [evidences, adminScope]
  );

  function matchesEvidenceFilters(evidence, excludedKeys = []) {
    const excluded = new Set(excludedKeys);
    const evidenceDate = normalizeDateValue(evidence?.meetingDate);

    const matchesMediator =
      excluded.has('selectedMediator') ||
      currentFilters.selectedMediatorIds.length === 0 ||
      getEvidenceMediatorIds(evidence).some(id =>
        currentFilters.selectedMediatorIds.map(String).includes(id)
      );
    const matchesMeetingType =
      excluded.has('selectedMeetingTypeId') ||
      matchesAnySelected(currentFilters.selectedMeetingTypeIds, getEvidenceMeetingTypeOptionId(evidence));
    const matchesFormativeProcess =
      excluded.has('selectedFormativeProcessCode') ||
      currentFilters.selectedFormativeProcessCodes.length === 0 ||
      getEvidenceValues(evidence, 'schoolCodes', 'schoolCode').some(code =>
        currentFilters.selectedFormativeProcessCodes.includes(code)
      );
    const matchesSchoolName =
      isCopScope ||
      excluded.has('selectedSchoolName') ||
      currentFilters.selectedSchoolNames.length === 0 ||
      getEvidenceValues(evidence, 'schoolNames', 'schoolName').some(name =>
        currentFilters.selectedSchoolNames.some(selected =>
          normalizeText(selected) === normalizeText(name)
        )
      );
    const matchesGroup =
      excluded.has('selectedGroupCode') ||
      currentFilters.selectedGroupCodes.length === 0 ||
      currentFilters.selectedGroupCodes.some(code => isCopScope
        ? normalizeText(getEvidenceSchoolName(evidence)) === normalizeText(code)
        : getEvidenceValues(evidence, 'groupCodes', 'groupCode').includes(code));
    const matchesEntity = currentFilters.selectedEntityTypes.length === 0 ||
      currentFilters.selectedEntityTypes.some(type => matchesEntityType(evidence, type));
    const matchesFromDate =
      excluded.has('fromDate') ||
      !currentFilters.fromDate ||
      (evidenceDate && evidenceDate >= currentFilters.fromDate);
    const matchesToDate =
      excluded.has('toDate') ||
      !currentFilters.toDate ||
      (evidenceDate && evidenceDate <= currentFilters.toDate);

    return (
      matchesMediator &&
      matchesMeetingType &&
      matchesFormativeProcess &&
      matchesSchoolName &&
      matchesEntity &&
      matchesGroup &&
      matchesFromDate &&
      matchesToDate
    );
  }

  const mediatorOptions = useMemo(
    () =>
      buildMediatorOptions(
        scopedEvidences.filter(evidence =>
          matchesEvidenceFilters(evidence, ['selectedMediator'])
        )
      ),
    [scopedEvidences, currentFilters]
  );

  const mediatorFilterOptions = useMemo(
    () => [{ id: ALL_FILTER_ID, name: 'Todos los mediadores' }, ...mediatorOptions],
    [mediatorOptions]
  );

  const selectedMediatorLabel = useMemo(() => getMultiFilterLabel(mediatorFilterOptions, selectedMediatorIds), [mediatorFilterOptions, selectedMediatorIds]);

  const formativeProcessCodeOptions = useMemo(
    () =>
      buildUniqueNamedOptions(
        scopedEvidences
          .filter(evidence =>
            matchesEvidenceFilters(evidence, ['selectedFormativeProcessCode'])
          )
          .flatMap(evidence => getEvidenceValues(evidence, 'schoolCodes', 'schoolCode'))
      ),
    [scopedEvidences, currentFilters]
  );

  const formativeProcessFilterOptions = useMemo(
    () => [{ id: ALL_FILTER_ID, name: 'Todos los codigos' }, ...formativeProcessCodeOptions],
    [formativeProcessCodeOptions]
  );

  const schoolNameOptions = useMemo(
    () =>
      buildUniqueNamedOptions(
        scopedEvidences
          .filter(evidence =>
            matchesEvidenceFilters(evidence, ['selectedSchoolName'])
          )
          .flatMap(evidence => getEvidenceValues(evidence, 'schoolNames', 'schoolName'))
      ),
    [scopedEvidences, currentFilters]
  );

  const schoolNameFilterOptions = useMemo(
    () => [
      { id: ALL_FILTER_ID, name: 'Todas las instituciones/organizaciones' },
      ...schoolNameOptions
    ],
    [schoolNameOptions]
  );

  const groupCodeOptions = useMemo(
    () =>
      buildUniqueNamedOptions(
        scopedEvidences
          .filter(evidence =>
            matchesEvidenceFilters(evidence, ['selectedGroupCode'])
          )
          .flatMap(evidence =>
            isCopScope
              ? getEvidenceValues(evidence, 'schoolNames', 'schoolName')
              : getEvidenceValues(evidence, 'groupCodes', 'groupCode')
          )
      ),
    [scopedEvidences, currentFilters, isCopScope]
  );

  const groupFilterOptions = useMemo(
    () => [
      {
        id: ALL_FILTER_ID,
        name: isCopScope ? 'Todas las asesorías' : 'Todos los grupos'
      },
      ...groupCodeOptions
    ],
    [groupCodeOptions, isCopScope]
  );

  const availableMeetingTypeIds = useMemo(
    () =>
      new Set(
        evidences
          .filter(evidence =>
            matchesEvidenceByAdminScope(evidence, adminScope)
          )
          .filter(evidence =>
            matchesEvidenceFilters(evidence, ['selectedMeetingTypeId'])
          )
          .map(getEvidenceMeetingTypeOptionId)
          .filter(Boolean)
      ),
    [evidences, adminScope, currentFilters]
  );

  const meetingTypeFilterOptions = useMemo(
    () => [
      { id: ALL_FILTER_ID, name: 'Todos los tipos' },
      ...meetingTypeOptions.filter(option => availableMeetingTypeIds.has(option.id))
    ],
    [availableMeetingTypeIds]
  );

  const selectedMeetingTypeLabel = useMemo(
    () =>
      getMultiFilterLabel(meetingTypeFilterOptions, selectedMeetingTypeIds),
    [meetingTypeFilterOptions, selectedMeetingTypeIds]
  );
  const selectedProcessLabel = getMultiFilterLabel(formativeProcessFilterOptions, selectedFormativeProcessCodes);
  const selectedSchoolNameLabel = getMultiFilterLabel(schoolNameFilterOptions, selectedSchoolNames);
  const selectedGroupLabel = getMultiFilterLabel(groupFilterOptions, selectedGroupCodes);
  const selectedEntityTypeLabel = selectedEntityTypes.map(getEntityTypeFilterLabel).filter(Boolean).join(', ');

  const adminFiltersSearch = useMemo(
    () =>
      buildAdminEvidenceFiltersSearch({
        scope: adminScope,
        mediatorId: serializeMultiFilterValue(selectedMediatorIds),
        meetingTypeId: serializeMultiFilterValue(selectedMeetingTypeIds),
        formativeProcessCode: serializeMultiFilterValue(selectedFormativeProcessCodes),
        schoolName: isCopScope ? '' : serializeMultiFilterValue(selectedSchoolNames),
        groupCode: isCopScope ? '' : serializeMultiFilterValue(selectedGroupCodes),
        advisorName: isCopScope ? serializeMultiFilterValue(selectedGroupCodes) : '',
        fromDate,
        toDate
      }),
    [
      selectedMediatorIds,
      selectedMeetingTypeIds,
      selectedFormativeProcessCodes,
      selectedSchoolNames,
      selectedGroupCodes,
      adminScope,
      isCopScope,
      fromDate,
      toDate
    ]
  );

  const adminEvidencesBackTo = useMemo(
    () =>
      `/administrador/menu/evidencias${adminFiltersSearch ? `?${adminFiltersSearch}` : ''}`,
    [adminFiltersSearch]
  );

  const filteredEvidences = useMemo(
    () => scopedEvidences.filter(evidence => matchesEvidenceFilters(evidence)),
    [scopedEvidences, currentFilters]
  );

  const sortedEvidences = useMemo(() => {
    if (!evidenceSort.field) return filteredEvidences;

    return filteredEvidences
      .map((evidence, index) => ({ evidence, index }))
      .sort((left, right) => {
        let compareResult = 0;

        if (evidenceSort.field === EVIDENCE_SORT_FIELDS.DATE) {
          compareResult = compareNullableValues(
            parseEvidenceDateSortValue(left.evidence?.meetingDate),
            parseEvidenceDateSortValue(right.evidence?.meetingDate),
            evidenceSort.direction,
            (leftValue, rightValue) => leftValue - rightValue
          );
        }

        return compareResult || left.index - right.index;
      })
      .map(item => item.evidence);
  }, [filteredEvidences, evidenceSort.field, evidenceSort.direction]);

  const hasActiveFilters = Boolean(
    ['pf', 'diplom', 'ndn'].includes(adminScope) ||
    selectedMediatorIds.length > 0 ||
    selectedMeetingTypeIds.length > 0 ||
    selectedFormativeProcessCodes.length > 0 ||
    (!isCopScope && selectedSchoolNames.length > 0) ||
    selectedEntityTypes.length > 0 ||
    selectedGroupCodes.length > 0 ||
    fromDate ||
    toDate
  );

  function handleEvidenceDateSort() {
    setEvidenceSort(previousSort => {
      if (previousSort.field !== EVIDENCE_SORT_FIELDS.DATE) {
        return {
          field: EVIDENCE_SORT_FIELDS.DATE,
          direction: 'desc'
        };
      }

      return {
        field: EVIDENCE_SORT_FIELDS.DATE,
        direction: previousSort.direction === 'asc' ? 'desc' : 'asc'
      };
    });
  }

  function renderEvidenceDateSortButton() {
    const isActive = evidenceSort.field === EVIDENCE_SORT_FIELDS.DATE;
    const nextDirection =
      isActive && evidenceSort.direction === 'desc'
        ? 'asc'
        : 'desc';
    const directionLabel = nextDirection === 'asc' ? 'ascendente' : 'descendente';

    return (
      <button
        type='button'
        className={`evidences-sort-button${
          isActive ? ' evidences-sort-button--active' : ''
        }`}
        onClick={handleEvidenceDateSort}
        title={`Ordenar evidencias por fecha ${directionLabel}`}
        aria-label={`Ordenar evidencias por fecha ${directionLabel}`}
        aria-pressed={isActive}
      >
        <img
          className={`evidences-sort-button__icon${
            isActive && evidenceSort.direction === 'desc'
              ? ' evidences-sort-button__icon--desc'
              : ''
          }`}
          src={isActive ? ArrowBlueIcon : ArrowGreyIcon}
          alt=''
        />
      </button>
    );
  }

  function handleMediatorFilter(_, values) {
    setSelectedMediatorIds(normalizeMultiSelection(values));
  }

  function handleMeetingTypeFilter(_, values) {
    setSelectedMeetingTypeIds(normalizeMultiSelection(values));
  }

  function handleFormativeProcessFilter(_, values) {
    setSelectedFormativeProcessCodes(normalizeMultiSelection(values));
  }

  function handleSchoolNameFilter(_, values) {
    setSelectedSchoolNames(normalizeMultiSelection(values));
  }

  function handleEntityTypeFilter(_, values) {
    setSelectedEntityTypes(normalizeMultiSelection(values));
  }

  function handleGroupFilter(_, values) {
    setSelectedGroupCodes(normalizeMultiSelection(values));
  }

  function handleClearFilters() {
    setSelectedMediatorIds([]);
    setSelectedMeetingTypeIds([]);
    setSelectedFormativeProcessCodes([]);
    setSelectedSchoolNames([]);
    setSelectedEntityTypes([]);
    setSelectedGroupCodes([]);
    setFromDate('');
    setToDate('');
  }

  async function handleDownloadEvidence(evidence) {
    const evidenceImages = Array.isArray(evidence?.images) ? evidence.images : [];
    if (evidenceImages.length === 0) {
      toast('No hay evidencias para descargar', { type: 'warning' });
      return;
    }

    try {
      for (const [index, image] of evidenceImages.entries()) {
        const imageUrl = buildProtectedImageUrl(image?.id, false);
        const response = await fetchWithAuth(imageUrl);
        if (!response.ok) {
          throw new Error('EVIDENCE_DOWNLOAD_FAILED');
        }

        const fileBlob = await response.blob();
        const extension = resolveFileExtension(image);
        const slotIndex = resolveEvidenceSlotIndex(image?.name, index);
        const prefix = getEvidencePrefix(evidence, slotIndex);
        const municipalityId =
          schoolMetadataById?.[String(evidence?.schoolId ?? '').trim()]?.municipalityId ?? '';
        const orderValue =
          String(evidence?.type || '').trim().toUpperCase() === 'MEETING' ||
          isCopMeetingType(evidence?.type)
            ? evidence?.order
            : 1;
        const fileName = buildEvidenceDownloadName({
          prefix,
          evidenceType: evidence?.type,
          municipalityId,
          meetingDate: evidence?.meetingDate,
          orderValue,
          schoolCode: evidence?.schoolCode,
          groupCode: evidence?.groupCode,
          extension,
          fallbackFileName: image?.name,
          fallbackIndex: slotIndex + 1
        });

        downloadBlob(fileBlob, fileName);
      }

      toast('Descarga de evidencias iniciada', { type: 'success' });
    } catch (error) {
      console.error(error);
      toast('No se pudieron descargar las evidencias', { type: 'error' });
    }
  }

  async function downloadEvidenceArchive({
    evidenceIds = [],
    fallbackPrefix = 'evidencias',
    cutoffDate = '',
    unified = false,
    setLoading
  }) {
    if (typeof setLoading !== 'function') return;
    let progressToast;

    try {
      setLoading(true);

      progressToast = toast.loading('Preparando descarga de evidencias…', {
        style: {fontFamily: "'work sans regular', sans-serif"}
      });
      const archiveBlob = await buildBatchedEvidenceArchive({
        evidenceIds: evidenceIds.length
          ? evidenceIds
          : scopedEvidences
            .filter(evidence => evidence?.archiveEligible !== false)
            .map(evidence => evidence.id),
        cutoffDate,
        unified,
        onProgress: (completed, total) => toast.update(progressToast, {
          render: completed === total ? 'Preparando el ZIP final…' : `Preparando evidencias: ${completed} de ${total} encuentros. Mantén esta página abierta.`
        })
      });
      const fileName = `${fallbackPrefix}_${getCurrentIsoDateInBogota()}.zip`;

      downloadBlob(archiveBlob, fileName);
      toast('Descarga de evidencias iniciada', { type: 'success' });
    } catch (error) {
      console.error(error);
      toast(error?.message || 'No se pudo descargar el consolidado de evidencias', {
        type: 'error'
      });
    } finally {
      if (progressToast !== undefined) toast.dismiss(progressToast);
      setLoading(false);
    }
  }

  async function handleDownloadAllEvidences() {
    if (isDownloadingAnyArchive) return;

    const scopedEvidenceIds = scopedEvidences
      .filter(evidence => evidence?.archiveEligible !== false)
      .map(evidence => Number(evidence?.id))
      .filter(id => Number.isInteger(id) && id > 0);

    if (adminScope) {
      if (scopedEvidenceIds.length === 0) {
        toast('No hay evidencias para descargar', { type: 'warning' });
        return;
      }

      await downloadEvidenceArchive({
        evidenceIds: scopedEvidenceIds,
        fallbackPrefix: 'evidencias',
        setLoading: setIsDownloadingArchive
      });
      return;
    }

    await downloadEvidenceArchive({
      fallbackPrefix: 'evidencias',
      setLoading: setIsDownloadingArchive
    });
  }

  async function handleDownloadAllUnifiedEvidences() {
    if (isDownloadingAnyArchive) return;

    const scopedEvidenceIds = scopedEvidences
      .filter(evidence => evidence?.archiveEligible !== false)
      .map(evidence => Number(evidence?.id))
      .filter(id => Number.isInteger(id) && id > 0);

    if (adminScope) {
      if (scopedEvidenceIds.length === 0) {
        toast('No hay evidencias para descargar', { type: 'warning' });
        return;
      }

      await downloadEvidenceArchive({
        evidenceIds: scopedEvidenceIds,
        fallbackPrefix: 'evidencias_unificadas',
        unified: true,
        setLoading: setIsDownloadingUnifiedArchive
      });
      return;
    }

    await downloadEvidenceArchive({
      fallbackPrefix: 'evidencias_unificadas',
      unified: true,
      setLoading: setIsDownloadingUnifiedArchive
    });
  }

  async function handleDownloadFilteredEvidences() {
    if (isDownloadingAnyArchive) return;
    await confirmFilteredDownload(false);
  }

  async function handleDownloadFilteredUnifiedEvidences() {
    if (isDownloadingAnyArchive) return;
    await confirmFilteredDownload(true);
  }

  async function confirmFilteredDownload(unified) {
    const filteredEvidenceIds = filteredEvidences
      .filter(evidence => evidence?.archiveEligible !== false)
      .map(evidence => Number(evidence?.id))
      .filter(id => Number.isInteger(id) && id > 0);

    if (filteredEvidenceIds.length === 0) {
      toast('No hay evidencias filtradas para descargar', { type: 'warning' });
      return;
    }

    await downloadEvidenceArchive({
      evidenceIds: filteredEvidenceIds,
      fallbackPrefix: unified
        ? 'evidencias_filtradas_unificadas'
        : 'evidencias_filtradas',
      cutoffDate: String(toDate || '').trim(),
      unified,
      setLoading: unified
        ? setIsDownloadingFilteredUnifiedArchive
        : setIsDownloadingFilteredArchive
    });
  }

  useEffect(() => {
    let cancelled = false;

    async function loadPageData() {
      setIsLoadingEvidences(true);

      const [evidencesData, schoolsData] = await Promise.all([
        getAllMeetingEvidences(),
        getSchools()
      ]);

      if (cancelled) return;

      if (Array.isArray(evidencesData)) {
        setEvidences(evidencesData);
      } else if (Array.isArray(evidencesData?.data)) {
        setEvidences(evidencesData.data);
      } else {
        setEvidences([]);
      }

      const nextSchoolMetadataById = Array.isArray(schoolsData)
        ? schoolsData.reduce((acc, school) => {
            const schoolId = String(school?.id ?? '').trim();
            if (!schoolId) {
              return acc;
            }

            acc[schoolId] = {
              municipalityId:
                school?.municipalityId ??
                school?.municipality_id ??
                school?.municipality?.id ??
                ''
            };

            return acc;
          }, {})
        : {};

      setSchoolMetadataById(nextSchoolMetadataById);

      setIsLoadingEvidences(false);
    }

    loadPageData();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const currentSearch = searchParams.toString();
    if (currentSearch === adminFiltersSearch) return;

    const nextSearchParams = new URLSearchParams(adminFiltersSearch);
    setSearchParams(nextSearchParams, { replace: true });
  }, [adminFiltersSearch, searchParams, setSearchParams]);

  return (
    <section className='evidences-admin'>
      <div className='filters-container'>
        <div className='filters-top'>
          <p className='title'>FILTROS DE BUSQUEDA DE EVIDENCIAS</p>
          <div className='filters-top-actions'>
            <button
              type='button'
              className='filters-clear-button'
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              Limpiar filtros
            </button>
            <button
              type='button'
              className='download-archive-button'
              onClick={handleDownloadAllEvidences}
              title={areGlobalDownloadsTemporarilyDisabled
                ? 'Este botón está temporalmente inhabilitado'
                : 'Descargar evidencias'}
              disabled={
                areGlobalDownloadsTemporarilyDisabled ||
                isLoadingEvidences ||
                isDownloadingAnyArchive ||
                scopedEvidences.length === 0
              }
            >
              {isDownloadingArchive ? 'Descargando...' : 'Descargar evidencias'}
            </button>
            <button
              type='button'
              className='download-archive-button'
              onClick={handleDownloadAllUnifiedEvidences}
              title={areGlobalDownloadsTemporarilyDisabled
                ? 'Este botón está temporalmente inhabilitado'
                : 'Descargar evidencias unificadas'}
              disabled={
                areGlobalDownloadsTemporarilyDisabled ||
                isLoadingEvidences ||
                isDownloadingAnyArchive ||
                scopedEvidences.length === 0
              }
            >
              {isDownloadingUnifiedArchive
                ? 'Descargando...'
                : 'Descargar evidencias unificadas'}
            </button>
            {areGlobalDownloadsTemporarilyDisabled && (
              <p className='downloads-temporarily-disabled-message'>
                Este botón está temporalmente inhabilitado
              </p>
            )}
          </div>
        </div>
        <div className='filters-content'>
          <AdminProcessScopeFilter onScopeChange={handleClearFilters} />
          <div className='filters-row'>
            <CustomFilterSelect
              optionsList={mediatorFilterOptions}
              placeholder={'Mediador'}
              addInfo={handleMediatorFilter}
              valueEdit={selectedMediatorLabel}
              multiple={true}
              selectedValues={selectedMediatorIds}
              searchable={true}
              searchPlaceholder={'Buscar mediador'}
            />
            <CustomFilterSelect
              optionsList={meetingTypeFilterOptions}
              placeholder={isCopScope ? 'Tipo de asesoría' : 'Tipo de encuentro'}
              addInfo={handleMeetingTypeFilter}
              valueEdit={selectedMeetingTypeLabel}
              multiple={true}
              selectedValues={selectedMeetingTypeIds}
            />
            <CustomFilterSelect
              optionsList={formativeProcessFilterOptions}
              placeholder={`Codigo ${isCopScope ? 'de Asesoría' : 'Proceso Formativo'}`}
              addInfo={handleFormativeProcessFilter}
              valueEdit={selectedProcessLabel}
              multiple={true}
              selectedValues={selectedFormativeProcessCodes}
              searchable={true}
              searchPlaceholder={'Buscar codigo'}
            />
            {!isCopScope && (
              <CustomFilterSelect
                optionsList={schoolNameFilterOptions}
                placeholder={'Institucion/organizacion'}
                addInfo={handleSchoolNameFilter}
                valueEdit={selectedSchoolNameLabel}
                multiple={true}
                selectedValues={selectedSchoolNames}
                searchable={true}
                searchPlaceholder={'Buscar institucion u organizacion'}
              />
            )}
            {!isCopScope && (
              <CustomFilterSelect
                optionsList={ENTITY_TYPE_FILTER_OPTIONS}
                placeholder='Tipo de entidad'
                addInfo={handleEntityTypeFilter}
                valueEdit={selectedEntityTypeLabel}
                multiple={true}
                selectedValues={selectedEntityTypes}
              />
            )}
            <CustomFilterSelect
              optionsList={groupFilterOptions}
              placeholder={isCopScope ? 'Nombre Asesoría' : 'Grupo'}
              addInfo={handleGroupFilter}
              valueEdit={selectedGroupLabel}
              multiple={true}
              selectedValues={selectedGroupCodes}
              searchable={true}
              searchPlaceholder={isCopScope ? 'Buscar asesoría' : 'Buscar grupo'}
            />
          </div>
          <div className='date-range'>
            <label className='label date-field' htmlFor='evidences-date-from'>
              <p className='input-title'>Desde</p>
              <input
                id='evidences-date-from'
                type='date'
                className='input-form'
                aria-label='Desde'
                title='Desde'
                value={fromDate}
                onChange={event => setFromDate(event.target.value)}
              />
            </label>
            <label className='label date-field' htmlFor='evidences-date-to'>
              <p className='input-title'>Hasta</p>
              <input
                id='evidences-date-to'
                type='date'
                className='input-form'
                aria-label='Hasta'
                title='Hasta'
                value={toDate}
                onChange={event => setToDate(event.target.value)}
              />
            </label>
          </div>
        </div>
      </div>

      <div className='evidences-main-container'>
        <div className='evidences-top'>
          <div className='evidences-title-actions'>
            <p className='title'>EVIDENCIAS</p>
            {renderEvidenceDateSortButton()}
          </div>
          <div className='evidences-top-actions'>
            <p className='paginator-text'>
              Mostrando <span>{filteredEvidences.length}</span> resultados
            </p>
            <button
              type='button'
              className='evidences-download-button'
              onClick={handleDownloadFilteredEvidences}
              title='Descargar evidencias filtradas'
              aria-label='Descargar evidencias filtradas'
              disabled={
                isLoadingEvidences ||
                isDownloadingAnyArchive ||
                filteredEvidences.length === 0
              }
            >
              <img src={PdfIcon} alt='Descargar evidencias filtradas' />
            </button>
            <button
              type='button'
              className='evidences-download-button evidences-download-button--unified'
              onClick={handleDownloadFilteredUnifiedEvidences}
              title='Descargar evidencias filtradas unificadas'
              aria-label='Descargar evidencias filtradas unificadas'
              disabled={
                isLoadingEvidences ||
                isDownloadingAnyArchive ||
                filteredEvidences.length === 0
              }
            >
              <img src={PdfIcon} alt='Descargar evidencias filtradas unificadas' />
            </button>
          </div>
        </div>
        <div className='evidences-content'>
          {isLoadingEvidences ? (
            <p className='evidences-state'>Cargando evidencias...</p>
          ) : filteredEvidences.length === 0 ? (
            <p className='evidences-state'>
              {hasActiveFilters
                ? 'No hay evidencias que coincidan con los filtros.'
                : 'Aun no hay evidencias para mostrar.'}
            </p>
          ) : (
            <div
              className={`evidences-table${
                isCopScope ? ' evidences-table--cop' : ''
              }`}
            >
              <div className='evidences-row evidences-row--head'>
                <p className='evidence-cell'>
                  {`Codigo de ${isCopScope ? 'Asesoría' : 'Proceso Formativo'}`}
                </p>
                {!isCopScope && <p className='evidence-cell'>Grupo</p>}
                <p className='evidence-cell'>Encuentro</p>
                <p className='evidence-cell'>Mediador</p>
                <p className='evidence-cell'>
                  {isCopScope ? 'Nombre Asesoría' : 'Nombre Institucion'}
                </p>
                <p className='evidence-cell'>Fecha</p>
                <p className='evidence-cell'>Acciones</p>
              </div>

              {sortedEvidences.map(evidence => {
                const detailPath = getEvidenceDetailPath(evidence, adminEvidencesBackTo);

                return (
                  <div className='evidences-row' key={`${evidence?.sourceType || evidence?.type}-${evidence?.id}`}>
                    <p className='evidence-cell' data-label={`Codigo de ${isCopScope ? 'Asesoría' : 'Proceso Formativo'}`}>{evidence?.schoolCode || '-'}</p>
                    {!isCopScope && (
                      <p className='evidence-cell' data-label='Grupo'>{evidence?.groupCode || '-'}</p>
                    )}
                    <p className='evidence-cell' data-label='Encuentro'>{evidence?.meetingName || '-'}</p>
                    <p className='evidence-cell' data-label='Mediador'>{evidence?.mediatorName || '-'}</p>
                    <p className='evidence-cell' data-label={isCopScope ? 'Nombre Asesoría' : 'Nombre Institucion'}>
                      {getEvidenceSchoolName(evidence) || '-'}
                    </p>
                    <p className='evidence-cell' data-label='Fecha'>{normalizeDateValue(evidence?.meetingDate) || '-'}</p>
                    <div className='evidence-cell evidence-cell--actions' data-label='Acciones'>
                      <Link
                        className='action-button'
                        to={detailPath}
                        state={{ backTo: adminEvidencesBackTo }}
                      >
                        <img src={BlueEyeIcon} alt='Ver evidencia' />
                      </Link>
                      <button
                        type='button'
                        className='action-button'
                        onClick={() => handleDownloadEvidence(evidence)}
                      >
                        <img src={PdfIcon} alt='Descargar evidencias' />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default EvidencesAdmin;
